import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase/client'
import type { Session } from '@supabase/supabase-js'

// Layout
import AppShell from '@/components/layout/AppShell'

// Pages
import Login from '@/pages/Login'
import Dashboard from '@/pages/Dashboard'
import LoftSelection from '@/pages/LoftSelection'
import NDTSelection from '@/pages/NDTSelection'
import CompanySelection from '@/pages/CompanySelection'
import EquipmentList from '@/pages/EquipmentList'
import InspectionFlow from '@/pages/InspectionFlow'
import ETTestForm from '@/pages/ETTestForm'
import MTTestForm from '@/pages/MTTestForm'
import VTTestForm from '@/pages/VTTestForm'
import PTTestForm from '@/pages/PTTestForm'
import LTTestForm from '@/pages/LTTestForm'
import UTTestForm from '@/pages/UTTestForm'
import RTTestForm from '@/pages/RTTestForm'
import CTHTestForm from '@/pages/CTHTestForm'
import ServiceReportForm from '@/pages/ServiceReportForm'
import ReportsPage from '@/pages/ReportsPage'

// Features
import { SamlelistePage, SamlelisteFlow } from '@/features/samleliste'

/** Wraps a page in the persistent AppShell, or redirects to login if no session. */
const Protected = ({
  session,
  children,
}: {
  session: Session | null
  children: React.ReactNode
}) => {
  if (!session) return <Navigate to="/login" replace />
  return <AppShell>{children}</AppShell>
}

function App() {
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      setLoading(false)
    })

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session)
    })

    return () => subscription.unsubscribe()
  }, [])

  if (loading) {
    return (
      <div
        className="flex items-center justify-center h-screen"
        style={{ backgroundColor: 'var(--bg-primary)' }}
      >
        <div className="w-10 h-10 border-4 border-t-transparent rounded-full animate-spin"
          style={{ borderColor: 'var(--color-primary)', borderTopColor: 'transparent' }}
        />
      </div>
    )
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route
          path="/login"
          element={!session ? <Login /> : <Navigate to="/dashboard" replace />}
        />
        <Route
          path="/dashboard"
          element={<Protected session={session}><Dashboard /></Protected>}
        />
        <Route
          path="/loft-selection"
          element={<Protected session={session}><LoftSelection /></Protected>}
        />
        <Route
          path="/ndt-selection"
          element={<Protected session={session}><NDTSelection /></Protected>}
        />
        <Route
          path="/company"
          element={<Protected session={session}><CompanySelection /></Protected>}
        />
        <Route
          path="/equipment"
          element={<Protected session={session}><EquipmentList /></Protected>}
        />
        <Route
          path="/inspect/:equipmentId"
          element={<Protected session={session}><InspectionFlow /></Protected>}
        />
        <Route
          path="/et-test"
          element={<Protected session={session}><ETTestForm /></Protected>}
        />
        <Route
          path="/mt-test"
          element={<Protected session={session}><MTTestForm /></Protected>}
        />
        <Route
          path="/vt-test"
          element={<Protected session={session}><VTTestForm /></Protected>}
        />
        <Route
          path="/pt-test"
          element={<Protected session={session}><PTTestForm /></Protected>}
        />
        <Route
          path="/lt-test"
          element={<Protected session={session}><LTTestForm /></Protected>}
        />
        <Route
          path="/ut-test"
          element={<Protected session={session}><UTTestForm /></Protected>}
        />
        <Route
          path="/rt-test"
          element={<Protected session={session}><RTTestForm /></Protected>}
        />
        <Route
          path="/thread-test"
          element={<Protected session={session}><CTHTestForm /></Protected>}
        />
        <Route
          path="/service-report"
          element={<Protected session={session}><ServiceReportForm /></Protected>}
        />
        <Route
          path="/reports"
          element={<Protected session={session}><ReportsPage /></Protected>}
        />
        <Route
          path="/samleliste-flow"
          element={<Protected session={session}><SamlelisteFlow /></Protected>}
        />
        <Route
          path="/samleliste/:companyId"
          element={<Protected session={session}><SamlelistePage /></Protected>}
        />
        <Route path="/" element={<Navigate to="/login" replace />} />
      </Routes>
    </BrowserRouter>
  )
}

export default App

